/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNotification } from '@/containers'
import { useLightbox } from '@/hooks/useLightBox'
import type { ProductReview } from '@/types'
import dayjs from 'dayjs'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import ImageOptimize from '../ImageOptimize'

const StarRatings = dynamic(() => import('react-star-ratings'), {
  ssr: false,
})

interface ReviewAndRateType {
  data: ProductReview
  bottomDivider: boolean
}

const breakpoints = [440, 414, 375]

export const ReviewAndRateItem = ({
  data,
  bottomDivider,
}: ReviewAndRateType) => {
  const { systemConfig } = useNotification()
  const { openLightBox, renderLightBox } = useLightbox()
  const [lightBoxData, setLighBoxData] = useState<
    | {
        index: number
        images: string[]
      }
    | undefined
  >(undefined)

  const handleOpenLightBox = (index: number, images: string[]) => {
    setLighBoxData({ images, index })
    openLightBox(index)
  }

  return (
    <div className='commentBox relative'>
      <div className='relative rounded-lg'>
        <div className='flex items-center justify-between'>
          <h5 className='text-sm font-bold text-main'>{data.user?.name}</h5>
          <span className='text-xs italic text-sub'>
            {dayjs(data.createdAt).format('HH:mm:ss - DD/MM/YYYY')}
          </span>
        </div>

        <div className='timeRate flex items-center gap-2'>
          <StarRatings
            starRatedColor='#FFCD4B'
            numberOfStars={5}
            rating={data.point}
            starDimension='10px'
            starSpacing='0px'
            svgIconPath='M20 0L26.1803 13.1672L40 15.2786L30 25.5279L32.3607 40L20 33.1672L7.63932 40L10 25.5279L0 15.2786L13.8197 13.1672L20 0Z'
            svgIconViewBox='0 0 40 40'
          />
        </div>
        <div className='text-sm text-main'>{data.content}</div>

        {data.images?.length > 0 && (
          <ul className='text-black mt-1.5 flex gap-2 text-sm font-normal'>
            {data.images.slice(0, 5).map((imageItem, imageIndex) => (
              <li
                className='aspect-square max-w-[60px] flex-[60px] cursor-pointer overflow-hidden rounded-sm'
                key={imageIndex}
                onClick={() => handleOpenLightBox(imageIndex, data.images)}
              >
                <ImageOptimize
                  src={imageItem || ''}
                  width={70}
                  height={70}
                  alt={data.content}
                  className='h-full w-full object-cover'
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {data.staff && data.answerContent && (
        <div className='mt-3 rounded-lg bg-[#F3F4F6] px-3 pb-3 pt-2'>
          <div className='flex items-center gap-2 text-sm font-semibold text-main'>
            <span>{systemConfig?.authorDefault || 'Điện máy Shop1'}</span>
            <span className='flex h-3 w-3 items-center justify-center overflow-hidden rounded-full bg-primary'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={8}
                height={8}
                viewBox='0 0 11 11'
                fill='none'
              >
                <g clipPath='url(#clip0_590_6242)'>
                  <path
                    d='M10.5176 2.15709C10.2082 1.84728 9.70582 1.84747 9.39601 2.15709L4.34787 7.20542L2.10419 4.96176C1.79438 4.65195 1.29217 4.65195 0.98236 4.96176C0.672547 5.27158 0.672547 5.77378 0.98236 6.08359L3.78684 8.88807C3.94165 9.04288 4.14464 9.12048 4.34765 9.12048C4.55067 9.12048 4.75386 9.04307 4.90867 8.88807L10.5176 3.2789C10.8275 2.9693 10.8275 2.46689 10.5176 2.15709Z'
                    fill='white'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_590_6242'>
                    <rect
                      width={10}
                      height={10}
                      fill='white'
                      transform='translate(0.75 0.522705)'
                    />
                  </clipPath>
                </defs>
              </svg>
            </span>
          </div>

          {!!data.answerContent && (
            <div className='text-sm text-main'>{data.answerContent}</div>
          )}

          <ul className='text-black mt-1 flex gap-2 text-base font-normal'>
            {data.answerImages &&
              data.answerImages.slice(0, 5).map((imageItem, imageIndex) => {
                return (
                  <li
                    className='aspect-square max-w-[60px] flex-[60px] cursor-pointer overflow-hidden rounded-sm'
                    key={imageIndex}
                    onClick={() =>
                      handleOpenLightBox(imageIndex, data.answerImages)
                    }
                  >
                    <ImageOptimize
                      src={imageItem || ''}
                      width={60}
                      height={60}
                      alt={data.content}
                      className='h-full w-full object-cover'
                    />
                  </li>
                )
              })}
          </ul>
        </div>
      )}

      {bottomDivider && <div className='mt-3 h-[1px] w-full bg-[#D1D5DB]' />}

      {!!lightBoxData &&
        renderLightBox({
          slides: lightBoxData.images?.map((e: any) => {
            const width = 475 * 4
            const height = 475 * 4
            return {
              src: e,
              width,
              height,
              srcSet: breakpoints.map(breakpoint => {
                const breakpointHeight = Math.round(
                  (height / width) * breakpoint
                )
                return {
                  src: e,
                  width: breakpoint,
                  height: breakpointHeight,
                }
              }),
            }
          }),
          index: lightBoxData.index,
        })}
    </div>
  )
}

export default ReviewAndRateItem
